import React, { useContext } from 'react'
import Const from '../Consts'
import { AuthorInfo } from './AuthorInfo'
import { Contract } from './Contract'
import { IndividualRating, OverallRating } from '../review_ratings'
import { ReviewSource } from './ReviewSource'
import { ReviewReply } from './review_reply'
import { editReviewPath, newEntryPath, supplierPagePath } from '../routes/path'
import type { Review as ReviewType, ReviewReply as ReviewReplyType } from '../supplier_show/prop_shapes/review'
import { AuthContext, canReportAbuse } from 'components/common/auth/AuthContext'
import clsx from 'clsx'
import { SupplierContext } from 'components/supplier_show/SupplierContext'

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const year = new Intl.DateTimeFormat('en-US', { year: '2-digit' }).format(date)
  const month = new Intl.DateTimeFormat('en-US', { month: 'numeric' }).format(date)
  const day = new Intl.DateTimeFormat('en-US', { day: '2-digit' }).format(date)

  return `${month}/${day}/${year}`
}

interface AbuseReportFlagProps {
  review: ReviewType
  canReportAbuse: boolean
}

function AbuseReportFlag ({ review, canReportAbuse }: AbuseReportFlagProps): React.ReactElement {
  return canReportAbuse
    ? (
      <a
        href={newEntryPath(review.id, 'Review')}
        className='pro-text-xs unstyled-tip-label'
        data-remote='true'
        data-tooltip
        title='Report Abuse'
      >
        <i className='fal fa-flag' aria-hidden />
        <span className='show-for-sr'>Report Abuse</span>
      </a>
      )
    : <></>
}

interface ReviewTileFooterProps {
  review: ReviewType
}

function ReviewTileFooter ({ review }: ReviewTileFooterProps): React.ReactElement {
  const authContext = useContext(AuthContext)
  const showReportAbuse = canReportAbuse(authContext)
  const { contract, contract_vehicle: contractVehicle } = review
  return (
    <div className='grid-x review-footer'>
      <div className='contract-wrapper'>
        {(contract != null || contractVehicle != null) &&
          <Contract contract={contract} contractVehicle={contractVehicle} reviewCanEdit={review.can_edit} />}
      </div>
      <span className='flex-child-grow' />

      {review.can_edit && (
        <span className='review-control'>
          <a href={editReviewPath(review.id)} className='edit'>
            <i className='fa-solid fa-square-pen color-pro-steel-grey-700' aria-hidden='true' /> Edit
          </a>
        </span>
      )}

      {review.share_with_supplier && (
        <span className='review-control info-text'>Shared with Supplier</span>
      )}

      <span className='review-control info-text'>
        {formatDate(review.review_date)}
      </span>
      <span className='review-control info-text'>
        <ReviewSource review={review} />
      </span>
      <span className='review-control'>
        {AbuseReportFlag({ review, canReportAbuse: showReportAbuse })}
      </span>
    </div>
  )
}

const transformReviewReply = (serverReviewReply: any): ReviewReplyType => {
  const { id, text, updated_at: updatedAt } = serverReviewReply ?? {}
  const parsedDate = Date.parse(updatedAt)
  return {
    id,
    text,
    updatedAt: !Number.isNaN(parsedDate) ? new Date(updatedAt) : undefined
  }
}

interface ReviewTileProps {
  review: ReviewType
  displaySupplierName?: boolean
  noPadding?: boolean
}

export function ReviewTile ({ review, displaySupplierName = false, noPadding = false }: ReviewTileProps): JSX.Element {
  const { star_ratings: starRatings } = review

  const reviewReply = transformReviewReply(review.review_reply)

  const supplierContext = {
    supplierId: review.supplier_id,
    supplierName: review.supplier_name,
    logoUrl: review.supplier_logo_url
  }

  return (
    <SupplierContext.Provider value={supplierContext}>
      <div className={clsx('pro-rounded pro-closest-shadow review-tile margin-bottom-2', review.status, noPadding && 'unpadded')} id={`review-${review.id}`}>
        <div className='grid-x'>
          <div className='large-auto medium-12 cell author-info'>
            {displaySupplierName && (
              <h3 className='supplier-name'>
                <a href={supplierPagePath(review.supplier_slug ?? '')}>{review.supplier_name}</a>
              </h3>
            )}
            <AuthorInfo review={review} />
          </div>
          <div className='large-shrink medium-12 cell'>
            <div className='grid-x grid-margin-x'>
              <div className='large-shrink medium-4 small-12 cell'>
                <OverallRating ratingLabel={Const.RATING_TYPES.overall_rating} value={starRatings.overall_rating} />
              </div>
              <div className='large-shrink medium-4 small-6 cell ratings'>
                <div className='grid-y full-height'>
                  <IndividualRating title={Const.RATING_TYPES.service_rating} value={starRatings.service_rating} />
                  <IndividualRating title={Const.RATING_TYPES.price_rating} value={starRatings.price_rating} />
                </div>
              </div>
              <div className='large-shrink medium-4 small-6 cell ratings'>
                <div className='grid-y full-height'>
                  <IndividualRating title={Const.RATING_TYPES.quality_rating} value={starRatings.quality_rating} />
                  <IndividualRating title={Const.RATING_TYPES.timeliness_rating} value={starRatings.timeliness_rating} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr />
        {review.title !== undefined && (
          <div className='pro-text-s font-bold color-pro-steel-grey-700 margin-bottom-1'>{review.title}</div>
        )}
        <div className='pro-text-s color-pro-steel-grey-700 review-body' data-testid={`review-body-${review.id}`}>
          {review.body}
        </div>
        <ReviewTileFooter review={review} />
        <div className='grid-y full-height'>
          <ReviewReply reply={reviewReply} reviewId={review.id} />
        </div>
      </div>
    </SupplierContext.Provider>
  )
}
